<template>
  <main class="main main-home" role="main">
    <div id="scrollable/main" ref="mainScrollRef" class="main-home__inner">
      <section class="main-home__banner" aria-label="Banner">
        <Swiper
          class="main-home__banner-swiper"
          :autoplay="{
            delay: 5000,
            disableOnInteraction: false
          }"
          :pagination="pagination"
          :loop="true"
          @slideChange="handleBannerSlideChange"
        >
          <SwiperSlide v-if="(banners?.length ?? 0) === 0">
            <div class="main-home__banner-frame main-home__banner-frame--default">
              <img
                :src="require('@/assets/static/images/default-banner.jpg')"
                alt="Banner"
                decoding="async"
              />
            </div>
          </SwiperSlide>
          <SwiperSlide v-for="(item, index) in banners" v-else :key="'main_banner_' + index">
            <button type="button" class="main-home__banner-trigger" @click="bannerLinkTo(item)">
              <img :src="getBannerImageSrc(item, index)" alt="Banner" decoding="async" />
            </button>
          </SwiperSlide>
        </Swiper>
      </section>

      <template v-if="isMainContentReady && isExist">
        <button type="button" class="main-home__search" @click="push('/search')">
          <i class="i-search" aria-hidden="true"></i>
          <span>{{ $t('main.empty_searchBar') }}</span>
        </button>

        <aside class="main-notice-marquee" aria-label="공지사항">
          <strong class="main-notice-marquee__label">📢 {{ $t('main.notice_label') }}</strong>
          <div class="main-notice-marquee__track">
            <div class="main-notice-marquee__row">
              <p class="main-notice-marquee__text">
                <span
                  v-for="(part, index) in getFixedNoticeParts($t('main.fixed_notice'))"
                  :key="'fixed_notice_' + index"
                  :class="{ 'main-notice-marquee__highlight': part.isHighlight }"
                  v-text="part.text"
                ></span>
              </p>
              <p class="main-notice-marquee__text" aria-hidden="true">
                <span
                  v-for="(part, index) in getFixedNoticeParts($t('main.fixed_notice'))"
                  :key="'fixed_notice_copy_' + index"
                  :class="{ 'main-notice-marquee__highlight': part.isHighlight }"
                  v-text="part.text"
                ></span>
              </p>
            </div>
          </div>
        </aside>

        <section class="main-home__category">
          <NoData
            v-if="categoryList.length === 0"
            class="main-home__no-data"
            :message="$t('main.no_data.category')"
          />
          <ul v-else class="main-home__category-grid">
            <li class="main-home__category-item main-home__category-item--all">
              <button type="button" @click="push('/search/result')">
                <span class="main-home__category-image">
                  <img
                    :src="require('@/assets/static/images/sample/all-category.png')"
                    alt=""
                    decoding="async"
                  />
                </span>
                <span class="main-home__category-name">
                  {{ $t('admin.dashboard.section.order_rank.all') }}
                </span>
              </button>
            </li>
            <li
              v-for="(item, index) in categoryList"
              :key="'main_category_' + index"
              class="main-home__category-item"
              :class="{ 'main-home__category-item--featured': item.storeCategoryNo === 1 }"
            >
              <button type="button" @click="push('/search/result/' + item.storeCategoryNo)">
                <span class="main-home__category-image">
                  <img
                    v-if="item.file"
                    :src="getImagePath(item.file?.storedFileName ?? '', 'origin')"
                    alt=""
                    loading="eager"
                    fetchpriority="high"
                  />
                </span>
                <span class="main-home__category-name">
                  {{ Util.translator(item, 'category') }}
                </span>
                <span
                  v-if="item.storeCategoryNo === 1"
                  class="main-home__category-cue"
                  aria-hidden="true"
                >
                  {{ $t('main.featuredPromo.link') }}
                </span>
              </button>
            </li>
          </ul>
        </section>

        <section class="main-home__stores">
          <div class="main-home__tabs">
            <button
              type="button"
              :class="{ on: tab === MAIN_TAB.PROMOTION }"
              @click="swapTab(MAIN_TAB.PROMOTION)"
            >
              {{ $t('main.segmentedControl.promotion') }}
            </button>
            <button
              type="button"
              :class="{ on: tab === MAIN_TAB.NEW }"
              @click="swapTab(MAIN_TAB.NEW)"
            >
              {{ $t('main.segmentedControl.newShop') }}
            </button>
          </div>

          <div
            v-if="tab === MAIN_TAB.PROMOTION"
            v-observe-visibility="{
              callback: promotionStore,
              once: true
            }"
            class="main-home__store-panel"
          >
            <NoData
              v-if="promotionList.length === 0"
              class="main-home__no-data"
              :message="$t('main.no_data.promotion')"
            />
            <ul v-else class="main-home__store-list">
              <li
                v-for="(item, index) in promotionList"
                :key="'main_promotion_' + index"
                class="main-home__store-item"
                @click="toStoreMain('/shop/' + item.storeNo + '/product')"
              >
                <img
                  :src="
                    item?.store?.files
                      ? getImagePath(item?.store?.files[0]?.storedFileName, 'origin')
                      : defaultProductImage
                  "
                  alt="Store_image"
                  loading="lazy"
                  decoding="async"
                />
                <div class="main-home__store-copy">
                  <strong>{{ Util.translator(item.store, 'name') }}</strong>
                  <span
                    v-if="item.store.isWork"
                    class="main-home__store-status yd-badge yd-badge--business"
                    :class="[
                      item.store.businessStatus === 'OPEN'
                        ? 'main-home__store-status--open'
                        : 'main-home__store-status--holiday',
                      item.store.businessStatus.toLowerCase()
                    ]"
                  >
                    {{ $t('promotion.store_status.' + item.store.businessStatus.toLowerCase()) }}
                  </span>
                  <span
                    v-else
                    class="main-home__store-status main-home__store-status--close yd-badge yd-badge--business close"
                  >
                    {{ $t('promotion.store_status.close') }}
                  </span>
                  <p>{{ Util.translator(item, 'content') }}</p>
                </div>
              </li>
            </ul>
          </div>

          <div
            v-if="tab === MAIN_TAB.NEW"
            v-observe-visibility="{
              callback: newStore,
              once: true
            }"
            class="main-home__store-panel"
          >
            <NoData
              v-if="storeList.length === 0"
              class="main-home__no-data"
              :message="$t('main.no_data.new')"
            />
            <ul v-else class="main-home__store-list">
              <li
                v-for="(item, index) in storeList"
                :key="'main_new_store_' + index"
                class="main-home__store-item"
                @click="$router.push('/shop/' + item.storeNo + '/product')"
              >
                <img
                  :src="
                    item?.files
                      ? getImagePath(item?.files[0]?.storedFileName ?? '', 'origin')
                      : defaultProductImage
                  "
                  alt="Store Image"
                  loading="lazy"
                  decoding="async"
                />
                <div class="main-home__store-copy">
                  <strong>{{ Util.translator(item, 'name') }}</strong>
                  <span
                    v-if="item.isWork"
                    class="main-home__store-status yd-badge yd-badge--business"
                    :class="[
                      item.businessStatus === 'OPEN'
                        ? 'main-home__store-status--open'
                        : 'main-home__store-status--holiday',
                      item.businessStatus.toLowerCase()
                    ]"
                  >
                    {{ $t('promotion.store_status.' + item.businessStatus.toLowerCase()) }}
                  </span>
                  <span
                    v-else
                    class="main-home__store-status main-home__store-status--close yd-badge yd-badge--business close"
                  >
                    {{ $t('promotion.store_status.close') }}
                  </span>
                  <p>
                    <template
                      v-for="(schedule, scheduleIndex) in item.businessSchedule"
                      :key="'main_schedule_' + scheduleIndex"
                    >
                      {{ $t('store.info.business_time') }} :
                      {{ schedule.open.slice(0, -3) }} ~
                      {{ schedule.close.slice(0, -3) }}
                    </template>
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <button
            type="button"
            class="main-home__more"
            @click="tab === MAIN_TAB.PROMOTION ? push('/promotion') : push('/new')"
          >
            {{ $t('main.button.more') }}
          </button>
        </section>

        <template v-if="SHOW_LAST_BEST">
          <section
            v-observe-visibility="{ callback: lastBest, once: true }"
            class="main-home__best-order"
          >
            <div class="main-home__section-heading">
              <h2>{{ $t('main.section.best_order') }}</h2>
              <button type="button" @click="push('/bestOrder')">
                {{ $t('main.button.showAll') }}
                <i class="i-right"></i>
              </button>
            </div>
            <NoData
              v-if="lastBestOrderList.length === 0"
              class="main-home__no-data"
              :message="$t('main.no_data.best_order')"
            />
            <ul v-else class="main-home__product-list">
              <li
                v-for="(item, index) in lastBestOrderList"
                :key="'main_product_' + index"
                @click="push('/shop/' + item.storeNo + '/product/' + item.productNo)"
              >
                <img
                  :src="item?.file ? getImagePath(item.file.storedFileName, 'origin') : defaultProductImage"
                  alt="Product_image"
                  loading="lazy"
                  decoding="async"
                />
                <strong>{{ Util.translator(item, 'name') }}</strong>
                <span>{{ toVn(item.price * (1 - item.discountRate ?? 0)) }}</span>
              </li>
            </ul>
          </section>
        </template>
      </template>

      <section
        v-else-if="isMainContentReady"
        v-observe-visibility="{ callback: load, once: true }"
        class="main-home__service-area"
      >
        <h2>{{ $t('main.section.serviceable_area') }}</h2>
        <ul>
          <li v-for="(item, index) in serviceAreaList" :key="'serviceable_area_' + index">
            <button type="button" @click="fnServiceAreaSearch(item.locationNo)">
              {{ Util.translator(item, 'location') }}
            </button>
          </li>
        </ul>
      </section>

      <section
        v-if="isMainContentReady"
        ref="eventSectionRef"
        v-observe-visibility="eventVisibilityOptions"
        class="main-home__board"
      >
        <div class="main-home__section-heading">
          <h2>{{ $t('main.section.event') }}</h2>
          <button type="button" @click="push('/my/event')">
            {{ $t('main.button.showAll') }}
            <i class="i-right"></i>
          </button>
        </div>
        <NoData
          v-if="deferredSectionLoaded[DEFERRED_MAIN_SECTION.EVENT] && eventList.length === 0"
          class="main-home__no-data"
          :message="$t('main.no_data.event')"
        />
        <ul
          v-else-if="deferredSectionLoaded[DEFERRED_MAIN_SECTION.EVENT]"
          class="main-home__board-list"
        >
          <li
            v-for="(item, index) in eventList"
            :key="'main_event_' + index"
            @click="fnDetail('event', item.eventNo)"
          >
            <p>{{ Util.translator(item, 'title') }}</p>
            <span>{{ formatDate(item.regDate) }}</span>
          </li>
        </ul>
      </section>

      <section
        v-if="isMainContentReady"
        ref="noticeSectionRef"
        v-observe-visibility="noticeVisibilityOptions"
        class="main-home__board"
      >
        <div class="main-home__section-heading">
          <h2>{{ $t('main.section.notice') }}</h2>
          <button type="button" @click="push('/my/notice')">
            {{ $t('main.button.showAll') }}
            <i class="i-right"></i>
          </button>
        </div>
        <NoData
          v-if="deferredSectionLoaded[DEFERRED_MAIN_SECTION.NOTICE] && noticeList.length === 0"
          class="main-home__no-data"
          :message="$t('main.no_data.notice')"
        />
        <ul
          v-else-if="deferredSectionLoaded[DEFERRED_MAIN_SECTION.NOTICE]"
          class="main-home__board-list"
        >
          <li
            v-for="(item, index) in noticeList"
            :key="'main_notice_' + index"
            @click="fnDetail('notice', item.noticeNo)"
          >
            <p>{{ translator(item, 'title') }}</p>
            <span>{{ formatDate(item.regDate) }}</span>
          </li>
        </ul>
      </section>

      <section v-if="isMainContentReady" class="main-home__certification">
        <a href="http://online.gov.vn/Home/AppDetails/2488" target="_blank">
          <img
            :src="require('@/assets/static/images/certification-png.png')"
            alt="인증이미지"
            loading="lazy"
            decoding="async"
          />
        </a>
      </section>
    </div>
  </main>
</template>

<script setup>
import 'swiper/swiper.scss';
import dayjs from 'dayjs';
import Util from '@/common/utils/Util';
import NoData from '@/components/views/nodata/index';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { computed, nextTick, onActivated, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';

import { getImagePath, MAIN_TAB } from '@/common/constants/ConstCode';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { toVn } from '@/common/utils/Currency';
import store from '@/store';
import { modalList, show } from '@/common/utils/ControlModal';
import router from '@/router';
import { useNavigator } from '@/common/composables/useNavigator';
import SplashIntroManager from '@/common/utils/SplashIntroManager';
import { recoverScroll } from '@/common/utils/ScrollManager';

const pagination = {
  type: 'fraction'
};
const { translator, nativeLink, isApp } = Util;

const { push } = useRouter();
const $store = useStore();
const { toStoreMain } = useNavigator();

const POSITION_REFRESH_DELAY = 300;
const REALTIME_INIT_DELAY = 600;
const COORDINATE_PRECISION = 6;
const SHOW_LAST_BEST = false;
const MAIN_STATIC_CACHE_TTL = 1000 * 60 * 30;
const MAIN_LOCATION_CACHE_TTL = 1000 * 60 * 3;
const MAIN_INITIAL_REVEAL_FALLBACK_DELAY = 4500;
const MAIN_INITIAL_REVEAL_FALLBACK_KEY = 'main-initial-reveal';
const MAIN_STATIC_REQUEST_TIMEOUT = 5000;
const MAIN_LOCATION_REQUEST_TIMEOUT = 5000;
const BANNER_INITIAL_LOAD_COUNT = 3;
const BANNER_PRELOAD_AHEAD_COUNT = 2;
const BLANK_IMAGE_SRC = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
const FIXED_NOTICE_HIGHLIGHT = '7%';
const DEFERRED_MAIN_SECTION = {
  EVENT: 'event',
  NOTICE: 'notice'
};

const getFixedNoticeParts = (notice) =>
  String(notice ?? '')
    .split(/(7%)/)
    .filter(Boolean)
    .map((text) => ({ text, isHighlight: text === FIXED_NOTICE_HIGHLIGHT }));

let positionRefreshTimer = null;
let realtimeInitTimer = null;
let realtimeSseTimer = null;
let mainDataRefreshPromise = null;

const initialRevealState = reactive({
  mainDataReady: false,
  mainDataSettled: false,
  locationDataReady: false,
  locationDataSettled: false
});
const isInitializingRealtime = ref(false);
const hasInitializedRealtime = ref(false);
const hasCompletedInitialReveal = ref(false);
const hasUserScrolled = ref(false);
const lastPositionKey = ref('');
const highestLoadedBannerIndex = ref(BANNER_INITIAL_LOAD_COUNT - 1);
const mainScrollRef = ref(null);
const eventSectionRef = ref(null);
const noticeSectionRef = ref(null);
const deferredSectionVisibility = reactive({
  [DEFERRED_MAIN_SECTION.EVENT]: false,
  [DEFERRED_MAIN_SECTION.NOTICE]: false
});
const deferredSectionLoaded = reactive({
  [DEFERRED_MAIN_SECTION.EVENT]: false,
  [DEFERRED_MAIN_SECTION.NOTICE]: false
});

onMounted(async () => {
  mainScrollRef.value?.addEventListener('scroll', handleMainScroll, { passive: true });
  window.addEventListener('scroll', handleMainScroll, { passive: true });
  document.addEventListener('visibilitychange', handleAppVisible);
  window.addEventListener('focus', handleAppVisible, { passive: true });
  hasUserScrolled.value = getMainScrollTop() > 0;

  // FCM Push token refresh, app.vue 에서도 있음
  if (isApp()) {
    void $store.dispatch('user/SYNC_FCM_KEY').catch(() => null);
  }
  scheduleInitialRevealFallback();
  try {
    await prepareMain();
  } finally {
    schedulePositionRefresh(currentPosition.value);
  }
  queueRealtimeInitialize();

  if (hasUserScrolled.value) tryLoadDeferredSections();
});

onBeforeUnmount(() => {
  mainScrollRef.value?.removeEventListener('scroll', handleMainScroll);
  window.removeEventListener('scroll', handleMainScroll);
  document.removeEventListener('visibilitychange', handleAppVisible);
  window.removeEventListener('focus', handleAppVisible);
  if (positionRefreshTimer) window.clearTimeout(positionRefreshTimer);
  if (realtimeInitTimer) window.clearTimeout(realtimeInitTimer);
  if (realtimeSseTimer) window.clearTimeout(realtimeSseTimer);
  clearInitialRevealFallback();
});

nextTick(() => {
  fnShowAlertModal();
});

const tab = ref(MAIN_TAB.PROMOTION);
const defaultProductImage = require('@/assets/static/images/skeleton-food01.svg');

const isExist = computed(() => $store.getters['main/getIsExist']);
const banners = computed(() => $store.getters['banner/getBanners']);
const currentPosition = computed(() => $store.getters['user/getUserPosition']);
const tagList = computed(() => $store.getters['main/getTagList']);
const categoryList = computed(() => $store.getters['main/getCategoryList']);
const eventList = computed(() => $store.getters['board/getEventList']);
const noticeList = computed(() => $store.getters['board/getNoticeList']);
const activeCount = computed(() => $store.getters['chat/getActiveCount']);
const promotionList = computed(() => $store.getters['main/getPromotionList'].slice(0, 2));
const storeList = computed(() => $store.getters['main/getStoreList']);
const lastBestOrderList = computed(() => $store.getters['order/getLastBestOrderList'].slice(0, 3));
const serviceAreaList = computed(() => $store.getters['main/getServiceAreaList']);
const mainCacheMeta = computed(() => $store.getters['main/getCacheMeta']);
const bannerCacheMeta = computed(() => $store.getters['banner/getCacheMeta']);
const isMainContentReady = computed(() => initialRevealState.mainDataReady && initialRevealState.locationDataReady);
const eventVisibilityOptions = computed(() => {
  if (!isMainContentReady.value || deferredSectionLoaded[DEFERRED_MAIN_SECTION.EVENT]) return false;
  return {
    callback: (isVisible) => handleDeferredSectionVisibility(DEFERRED_MAIN_SECTION.EVENT, isVisible),
    throttle: 300,
    intersection: {
      threshold: 0.15
    }
  };
});
const noticeVisibilityOptions = computed(() => {
  if (!isMainContentReady.value || deferredSectionLoaded[DEFERRED_MAIN_SECTION.NOTICE]) return false;
  return {
    callback: (isVisible) => handleDeferredSectionVisibility(DEFERRED_MAIN_SECTION.NOTICE, isVisible),
    throttle: 300,
    intersection: {
      threshold: 0.15
    }
  };
});

const fnShowAlertModal = () => {
  const isOnAlert = store.getters['user/getIsOnAlert'];

  if (!isOnAlert) {
    show(modalList.NativeAlertStateModal, {
      confirmText: 'setting',
      confirmCallback: () => {
        if ($store.getters['user/getUserData']?.userNo > 0) {
          router.push({ name: 'MySettings' });
        } else {
          if (Util.isApp() && Util.isIPhone()) {
            Util.nativeGpsSetting();
          } else {
            Util.androidPermission();
          }
        }
      }
    });
  }
};

/*
프로모션 / 신규상점 탭 변경
 */
const swapTab = (selected) => (tab.value = selected);
const formatDate = (rawDate) => dayjs(rawDate).format('YYYY.MM.DD HH:mm');

/**
 * 상세 페이지 이동
 * @param boardType 게시판 타입
 * @param boardNo 게시글 번호
 */
const fnDetail = (boardType, boardNo) => {
  push(`/my/${boardType}/${boardNo}`);
};

const load = () => $store.dispatch('main/fetchServiceAreaList', { notLoading: true });
const fnServiceAreaSearch = (locationNo) => {
  push({
    path: '/search',
    query: { locationNo: locationNo }
  });
};

const bannerLinkTo = ({ url, isBlank }) => {
  if (!url) return;
  if (isBlank && isApp()) nativeLink(url);
  else if (isBlank && !isApp()) window.open(url, 'blank');
  else window.location.href = url;
};

const getBannerRealIndex = (swiper) => Number(swiper?.realIndex ?? swiper?.activeIndex ?? 0);
const updateLoadedBannerRange = (activeIndex = 0) => {
  const bannerCount = banners.value?.length ?? 0;
  if (bannerCount <= 0) {
    highestLoadedBannerIndex.value = BANNER_INITIAL_LOAD_COUNT - 1;
    return;
  }

  highestLoadedBannerIndex.value = Math.min(
    bannerCount - 1,
    Math.max(highestLoadedBannerIndex.value, activeIndex + BANNER_PRELOAD_AHEAD_COUNT)
  );
};
const handleBannerSlideChange = (swiper) => updateLoadedBannerRange(getBannerRealIndex(swiper));
const isBannerImageLoaded = (index) => index <= highestLoadedBannerIndex.value;
const getBannerImageSrc = (item, index) => {
  if (!isBannerImageLoaded(index)) return BLANK_IMAGE_SRC;
  return getImagePath(item.file?.storedFileName ?? '');
};

watch(
  banners,
  (items) => {
    highestLoadedBannerIndex.value = Math.min((items?.length ?? 0) - 1, BANNER_INITIAL_LOAD_COUNT - 1);
  },
  { immediate: true }
);

const isCacheExpired = (fetchedAt = 0, ttl = 0) => {
  if (!fetchedAt) return true;
  return Date.now() - Number(fetchedAt) >= ttl;
};

const markMainDataReady = (isReady = true) => {
  initialRevealState.mainDataReady = isReady;
  if (isReady) initialRevealState.mainDataSettled = true;
};

const markMainDataSettled = (isSettled = true) => {
  initialRevealState.mainDataSettled = isSettled;
};

const markLocationDataReady = (isReady = true) => {
  initialRevealState.locationDataReady = isReady;
  if (isReady) initialRevealState.locationDataSettled = true;
};

const markLocationDataSettled = (isSettled = true) => {
  initialRevealState.locationDataSettled = isSettled;
};

const markInitialRevealReady = () => {
  markMainDataReady();
  markLocationDataReady();
};

const clearInitialRevealFallback = () => {
  SplashIntroManager.clearFallback(MAIN_INITIAL_REVEAL_FALLBACK_KEY);
};

const sanitizeInitialCache = () => {
  const mainCachePatch = {};
  if (!Array.isArray(tagList.value)) {
    $store.commit('main/setTagList', []);
    mainCachePatch.tagListFetchedAt = 0;
  }
  if (!Array.isArray(categoryList.value)) {
    $store.commit('main/setCategoryList', []);
    mainCachePatch.categoryListFetchedAt = 0;
  }
  if (!Array.isArray(banners.value)) {
    $store.commit('banner/setBanners', []);
    $store.commit('banner/setCacheMeta', { fetchedAt: 0, locationKey: '' });
  }
  if (Object.keys(mainCachePatch).length > 0) {
    $store.commit('main/setCacheMeta', mainCachePatch);
  }
};

const hasMainStaticCache = () => {
  return (
    Array.isArray(tagList.value) &&
    Array.isArray(categoryList.value) &&
    Number(mainCacheMeta.value?.tagListFetchedAt ?? 0) > 0 &&
    Number(mainCacheMeta.value?.categoryListFetchedAt ?? 0) > 0
  );
};

const shouldRefreshTagList = () => isCacheExpired(mainCacheMeta.value?.tagListFetchedAt, MAIN_STATIC_CACHE_TTL);
const shouldRefreshCategoryList = () =>
  isCacheExpired(mainCacheMeta.value?.categoryListFetchedAt, MAIN_STATIC_CACHE_TTL);

const queueMainDataRefresh = () => {
  const requests = [];
  const requestConfig = { notLoading: true, timeout: MAIN_STATIC_REQUEST_TIMEOUT };
  if (shouldRefreshTagList()) requests.push($store.dispatch('main/fetchTagList', requestConfig));
  if (shouldRefreshCategoryList()) requests.push($store.dispatch('main/fetchStoreCategoryList', requestConfig));
  if (requests.length === 0) return null;

  if (!mainDataRefreshPromise) {
    mainDataRefreshPromise = Promise.allSettled(requests).finally(() => {
      mainDataRefreshPromise = null;
    });
  }

  return mainDataRefreshPromise;
};

/**
 * 메인 화면 초기화 작업
 */
const prepareMain = async () => {
  markMainDataReady(false);
  markMainDataSettled(false);
  sanitizeInitialCache();
  $store.commit('chat/resetSelected');
  $store.commit('main/initList');
  $store.commit('main/setStoreList', []);
  $store.commit('store/initStoreList');

  const canUseCachedMainData = hasMainStaticCache();
  if (canUseCachedMainData) markMainDataReady();

  // TODO API 지연 테스트
  // await new Promise((resolve) => setTimeout(resolve, 10000));

  const refreshPromise = queueMainDataRefresh();

  if (!refreshPromise) {
    markMainDataReady();
    return;
  }

  if (canUseCachedMainData) {
    void refreshPromise;
    return;
  }

  try {
    await refreshPromise;
  } finally {
    markMainDataSettled();
    if (hasMainStaticCache()) markMainDataReady();
    else requestInitialRevealReloadModal();
  }
};

const promotionStore = (isVisible) => {
  if (!isVisible) return;
  $store.dispatch('main/fetchPromotionList', { main: true, notLoading: true });
};
const newStore = (isVisible) => {
  if (!isVisible) return;
  $store.dispatch('main/FETCH_NEW_STORES', { notLoading: true });
};
const lastBest = (isVisible) => {
  if (!isVisible) return;
  store.commit('order/initLastBestOrder');
  $store.dispatch('order/fetchLastBestOrderList', { main: true, notLoading: true });
};

/**
 * 메인 공지
 * @param isVisible
 */
const getDeferredSectionRef = (type) => {
  if (type === DEFERRED_MAIN_SECTION.EVENT) return eventSectionRef.value;
  if (type === DEFERRED_MAIN_SECTION.NOTICE) return noticeSectionRef.value;
  return null;
};

const getMainScrollTop = () => {
  if (mainScrollRef.value) return mainScrollRef.value.scrollTop;
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
};

const isDeferredSectionInViewport = (type) => {
  if (deferredSectionVisibility[type]) return true;

  const section = getDeferredSectionRef(type);
  if (!section) return false;

  const rect = section.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  return rect.top <= viewportHeight * 0.95 && rect.bottom >= viewportHeight * 0.1;
};

const loadDeferredSection = (type) => {
  if (deferredSectionLoaded[type]) return;
  deferredSectionLoaded[type] = true;

  if (type === DEFERRED_MAIN_SECTION.EVENT) {
    $store.dispatch('board/FETCH_EVENT', { limit: 5, forAdmin: false, notLoading: true });
    return;
  }

  if (type === DEFERRED_MAIN_SECTION.NOTICE) {
    $store.dispatch('board/FETCH_NOTICE', { limit: 5, forAdmin: false, notLoading: true });
  }
};

const tryLoadDeferredSection = (type) => {
  if (!isMainContentReady.value || deferredSectionLoaded[type]) return;
  if (!hasUserScrolled.value && getMainScrollTop() <= 0) return;
  if (!isDeferredSectionInViewport(type)) return;
  loadDeferredSection(type);
};

const tryLoadDeferredSections = () => {
  tryLoadDeferredSection(DEFERRED_MAIN_SECTION.EVENT);
  tryLoadDeferredSection(DEFERRED_MAIN_SECTION.NOTICE);
};

const handleDeferredSectionVisibility = (type, isVisible) => {
  deferredSectionVisibility[type] = isVisible;
  if (!isVisible) return;
  tryLoadDeferredSection(type);
};

const handleMainScroll = () => {
  if (getMainScrollTop() <= 0) return;
  hasUserScrolled.value = true;
  tryLoadDeferredSections();
};

const hasValidPosition = (position = {}) => {
  const latitude = Number(position?.latitude ?? 0);
  const longitude = Number(position?.longitude ?? 0);
  return latitude !== 0 && longitude !== 0;
};

const getPositionKey = (position = {}) => {
  const latitude = Number(position?.latitude ?? 0).toFixed(COORDINATE_PRECISION);
  const longitude = Number(position?.longitude ?? 0).toFixed(COORDINATE_PRECISION);
  return `${latitude}:${longitude}`;
};

const hasLocationCache = (positionKey = '') => {
  return (
    !!positionKey &&
    mainCacheMeta.value?.locationKey === positionKey &&
    bannerCacheMeta.value?.locationKey === positionKey &&
    Number(mainCacheMeta.value?.locationFetchedAt ?? 0) > 0 &&
    Number(bannerCacheMeta.value?.fetchedAt ?? 0) > 0
  );
};

const isLocationCacheFresh = (positionKey = '') => {
  return (
    hasLocationCache(positionKey) &&
    !isCacheExpired(mainCacheMeta.value?.locationFetchedAt, MAIN_LOCATION_CACHE_TTL) &&
    !isCacheExpired(bannerCacheMeta.value?.fetchedAt, MAIN_LOCATION_CACHE_TTL)
  );
};

const refreshLocationDependentData = async (payload = {}) => {
  const requestConfig = { notLoading: true, timeout: MAIN_LOCATION_REQUEST_TIMEOUT, ...payload };
  const shouldUpdateInitialRevealState = !hasCompletedInitialReveal.value;
  if (shouldUpdateInitialRevealState) {
    markLocationDataReady(false);
    markLocationDataSettled(false);
  }
  try {
    return await Promise.allSettled([
      $store.dispatch('main/IS_STORE_EXIST', requestConfig),
      $store.dispatch('banner/FETCH_BANNER', requestConfig)
    ]);
  } finally {
    if (!shouldUpdateInitialRevealState) return;
    markLocationDataSettled();
    if (!hasValidPosition(currentPosition.value) || hasLocationCache(getPositionKey(currentPosition.value))) {
      markLocationDataReady();
    } else {
      requestInitialRevealReloadModal();
    }
  }
};

const waitForMainContentPaint = () =>
  new Promise((resolve) => {
    const scheduleFrame = window.requestAnimationFrame ?? ((callback) => window.setTimeout(callback, 16));
    scheduleFrame(() => scheduleFrame(resolve));
  });

const hideNativeIntro = () => {
  if (!isApp()) return;
  if (Util.isIPhone()) {
    SplashIntroManager.hideIntro(MAIN_INITIAL_REVEAL_FALLBACK_KEY);
    return;
  }
  window.setTimeout(() => SplashIntroManager.hideIntro(MAIN_INITIAL_REVEAL_FALLBACK_KEY), 250);
};

const createInitialRevealFallbackContext = () => ({
  delayMs: MAIN_INITIAL_REVEAL_FALLBACK_DELAY,
  hasPreparedMainData: initialRevealState.mainDataReady,
  hasSettledMainData: initialRevealState.mainDataSettled,
  hasResolvedLocationData: initialRevealState.locationDataReady,
  hasSettledLocationData: initialRevealState.locationDataSettled,
  positionValid: hasValidPosition(currentPosition.value),
  tagCount: Array.isArray(tagList.value) ? tagList.value.length : -1,
  categoryCount: Array.isArray(categoryList.value) ? categoryList.value.length : -1,
  bannerCount: Array.isArray(banners.value) ? banners.value.length : -1,
  mainCacheMeta: mainCacheMeta.value,
  bannerCacheMeta: bannerCacheMeta.value
});

const shouldShowInitialRevealReloadModal = () => {
  return (
    isApp() &&
    !hasCompletedInitialReveal.value &&
    initialRevealState.mainDataSettled &&
    initialRevealState.locationDataSettled &&
    !isMainContentReady.value
  );
};

const requestInitialRevealReloadModal = () => {
  SplashIntroManager.requestReloadModal(
    {
      reason: 'MAIN_INITIAL_REVEAL_DATA_UNAVAILABLE',
      getContext: createInitialRevealFallbackContext
    },
    shouldShowInitialRevealReloadModal
  );
};

const finalizeInitialReveal = async () => {
  if (hasCompletedInitialReveal.value || !isMainContentReady.value) return;
  hasCompletedInitialReveal.value = true;
  clearInitialRevealFallback();

  await nextTick();
  await waitForMainContentPaint();
  await store.dispatch('layout/updateIsLoading', { isLoading: false });

  hideNativeIntro();
};

const scheduleInitialRevealFallback = () => {
  SplashIntroManager.scheduleFallback(MAIN_INITIAL_REVEAL_FALLBACK_KEY, {
    delayMs: MAIN_INITIAL_REVEAL_FALLBACK_DELAY,
    reason: 'MAIN_INITIAL_REVEAL_FALLBACK_TIMEOUT',
    canRun: shouldShowInitialRevealReloadModal,
    getContext: createInitialRevealFallbackContext
  });
};

const schedulePositionRefresh = (position = {}) => {
  if (!hasValidPosition(position)) {
    markLocationDataReady();
    requestInitialRevealReloadModal();
    return;
  }
  const positionKey = getPositionKey(position);
  const canUseCachedLocationData = hasLocationCache(positionKey);
  if (canUseCachedLocationData && !initialRevealState.locationDataReady) {
    markLocationDataReady();
    requestInitialRevealReloadModal();
  }

  const hasCheckedPositionBefore = !!lastPositionKey.value;
  const isPositionChanged = hasCheckedPositionBefore && positionKey !== lastPositionKey.value;
  const shouldRefresh = isPositionChanged || !isLocationCacheFresh(positionKey);
  lastPositionKey.value = positionKey;
  if (!shouldRefresh) {
    requestInitialRevealReloadModal();
    return;
  }

  const shouldRefreshImmediately = !canUseCachedLocationData || !initialRevealState.locationDataReady;

  if (positionRefreshTimer) window.clearTimeout(positionRefreshTimer);
  if (shouldRefreshImmediately) {
    void refreshLocationDependentData({ notLoading: true });
    return;
  }

  positionRefreshTimer = window.setTimeout(() => {
    void refreshLocationDependentData({ notLoading: true });
    positionRefreshTimer = null;
  }, POSITION_REFRESH_DELAY);
};

const handleAppVisible = () => {
  if (document.visibilityState === 'hidden') return;
  if (!/^\/main(?:\/|$)/.test(router.currentRoute.value.path)) return;

  void queueMainDataRefresh();
  schedulePositionRefresh(currentPosition.value);
};

onActivated(() => {
  const recovered = recoverScroll('/main', { behavior: 'auto' });
  hasUserScrolled.value = (recovered?.top ?? getMainScrollTop()) > 0;
  handleAppVisible();
});

/* 2025-10-14 채팅 뉴마크 추가 시작*/
const initialize = async () => {
  if (isInitializingRealtime.value || hasInitializedRealtime.value) return;

  const userData = $store.getters['user/getUserData'];
  const accessToken = $store.getters['user/getAccessToken'];
  const { userNo } = userData ?? {};
  if (!userNo || !accessToken) return;

  isInitializingRealtime.value = true;
  try {
    await $store.dispatch('chat/FETCH_BUBBLE_COUNT', userNo);
    await initChat(userNo, accessToken);

    if (realtimeSseTimer) window.clearTimeout(realtimeSseTimer);
    realtimeSseTimer = window.setTimeout(() => {
      initSse(userNo, accessToken);
      realtimeSseTimer = null;
    }, 1000);
    hasInitializedRealtime.value = true;
  } finally {
    isInitializingRealtime.value = false;
  }
};

const queueRealtimeInitialize = () => {
  if (realtimeInitTimer || hasInitializedRealtime.value || isInitializingRealtime.value) return;
  realtimeInitTimer = window.setTimeout(() => {
    initialize();
    realtimeInitTimer = null;
  }, REALTIME_INIT_DELAY);
};

const initChat = (userNo, accessToken) => {
  return $store.dispatch('chat/chatInitialize', { userNo, accessToken });
};

const initSse = (userNo, accessToken) => {
  $store.dispatch('sse/emitterInitialize', { userNo, accessToken });
};

watch(
  () => [Number(currentPosition.value?.latitude ?? 0), Number(currentPosition.value?.longitude ?? 0)],
  ([latitude, longitude], [prevLatitude, prevLongitude]) => {
    if (latitude === prevLatitude && longitude === prevLongitude) return;
    schedulePositionRefresh({ latitude, longitude });
  }
);

watch(isMainContentReady, (isReady) => {
  if (!isReady) return;
  nextTick(() => {
    hasUserScrolled.value = getMainScrollTop() > 0;
    tryLoadDeferredSections();
  });
  void finalizeInitialReveal();
});

watch(activeCount, (newVal, oldVal) => {
  if (newVal <= 0 || newVal === oldVal) return;
  queueRealtimeInitialize();
});
/* 2025-10-14 채팅 뉴마크 추가 끝*/
</script>

<style scoped lang="scss">
.main-home {
  display: flex;
  width: 100%;
  height: 100%;
  background: #ffffff;
  color: #334155;
  font-size: 1.2rem;

  &__inner {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    height: 100%;
    padding: 1rem;
    overflow-y: auto;
    background: #ffffff;
    font-size: 1.2rem;
    overscroll-behavior-y: contain;
  }

  &__inner > .main-home__category,
  &__inner > .main-home__stores,
  &__inner > .main-home__best-order,
  &__inner > .main-home__service-area,
  &__inner > .main-home__board,
  &__inner > .main-home__certification {
    margin-top: 0;
    margin-bottom: 0;
  }

  &__inner,
  &__inner * {
    box-sizing: border-box;
  }

  button {
    font: inherit;
  }

  &__banner {
    display: block;
    flex: 0 0 auto;
    width: 100%;
    aspect-ratio: 2 / 1;
    overflow: hidden;
    border-radius: 1.2rem;
    background: #f3f6f8;
    box-shadow: 0 0.3rem 1.2rem rgba(15, 23, 42, 0.06);

    :deep(.swiper-container),
    :deep(.swiper) {
      width: 100%;
      height: 100%;
      overflow: hidden;
    }

    :deep(.swiper-wrapper) {
      height: 100%;
    }

    :deep(.swiper-slide) {
      height: 100%;
      background: #f3f6f8;
    }

    :deep(.swiper-pagination) {
      right: 0.8rem;
      bottom: 0.8rem;
      left: auto;
      width: auto;
      padding: 0.25rem 0.6rem;
      border-radius: 0.8rem;
      background: rgba(15, 23, 42, 0.58);
      color: #ffffff;
      font-size: 1.1rem;
      line-height: 1.3;
      pointer-events: none;
    }
  }

  &__banner-frame,
  &__banner-trigger {
    display: block;
    width: 100%;
    height: 100%;
    padding: 0;
    border: 0;
    background: #f3f6f8;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__banner-trigger:focus-visible {
    outline: 2px solid #38bdf8;
    outline-offset: -2px;
  }

  &__search {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    width: 100%;
    min-height: 4.4rem;
    padding: 0 1.2rem;
    border: 1px solid #d9e5eb;
    border-radius: 1rem;
    background: #f8fbfc;
    color: #64748b;
    font-size: 1.2rem;
    text-align: left;

    i {
      display: inline-block;
      flex: 0 0 1.7rem;
      width: 1.7rem;
      height: 1.7rem;
      background-color: #94a3b8;
      font-size: 0;
      -webkit-mask: url('~@/assets/static/images/i_search.svg') no-repeat center / contain;
      mask: url('~@/assets/static/images/i_search.svg') no-repeat center / contain;
    }

    span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &:focus-visible {
      border-color: #38bdf8;
      outline: 0;
      box-shadow: 0 0 0 0.2rem rgba(56, 189, 248, 0.12);
    }
  }

  .main-notice-marquee {
    width: 100%;
    min-width: 0;
    height: 2.4rem;
    min-height: 2.4rem;
    margin: 0;
    border-color: #d8edf5;
    border-radius: 0.8rem;
    background-color: #eef8fc;
  }

  .main-notice-marquee__label {
    border-right-color: #d8edf5;
    background-color: #eaf6fd;
    color: #475569;
  }

  .main-notice-marquee__track {
    min-width: 0;
  }

  .main-notice-marquee__text {
    color: #475569;
    font-weight: 400;
  }

  .main-notice-marquee__highlight {
    color: #d45454;
    font-weight: 700;
  }

  &__category {
    width: 100%;
    padding-top: 1rem;
    border-top: 0.3rem solid #dceff6;
  }

  &__category-grid {
    display: grid;
    grid-template-columns: minmax(0, 0.875fr) minmax(0, 0.875fr) minmax(0, 1.05fr) minmax(0, 0.95fr) minmax(0, 1.25fr);
    grid-template-rows: repeat(2, minmax(8.4rem, auto));
    grid-auto-rows: minmax(8.4rem, auto);
    grid-auto-flow: row;
    gap: 0.3rem 0;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__category-item {
    min-width: 0;
    overflow: visible;
    border: 1px solid #edf1f4;
    border-radius: 1rem;
    background: #fbfcfd;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.035);

    &--all {
      grid-column: 3;
      grid-row: 1;
    }

    > button {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: 0.4rem;
      width: 100%;
      height: 100%;
      padding: 0.5rem 0;
      border: 0;
      background: transparent;
      color: #475569;

      &:focus-visible {
        outline: 2px solid #38bdf8;
        outline-offset: -2px;
      }
    }

    &--featured {
      grid-column: 1 / span 2;
      grid-row: 1 / span 2;
      border-color: #e1f0f4;
      background: #f8fcfd;
      box-shadow: 0 1px 4px rgba(15, 23, 42, 0.045);

      > button {
        padding: 0.8rem;
      }

      .main-home__category-image {
        width: 7.2rem;
        height: 7.2rem;
      }

      .main-home__category-name {
        color: #0b87b4;
        font-size: 1.4rem;
        font-weight: 700;
      }

      .main-home__category-cue {
        margin-top: auto;
      }
    }
  }

  &__category-image {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.6rem;
    height: 3.6rem;
    overflow: hidden;
    border-radius: 0.8rem;
    background: #ffffff;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__category-name {
    display: block;
    width: 100%;
    min-height: 2.7em;
    flex: 0 0 auto;
    overflow: visible;
    overflow-wrap: anywhere;
    color: #475569;
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.25;
    text-align: center;
    word-break: normal;
  }

  &__category-cue {
    max-width: 100%;
    overflow: hidden;
    color: #0b87b4;
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
    pointer-events: none;
  }

  &__stores {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding-top: 1rem;
    border-top: 0.3rem solid #dceff6;
  }

  &__tabs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0;
    margin-bottom: 0.2rem;
    border-bottom: 1px solid #e8eef2;

    button {
      position: relative;
      height: 4rem;
      border: 0;
      background: transparent;
      color: #64748b;
      font-size: 1.4rem;
      font-weight: 600;

      &.on {
        color: #0b91bf;
        font-weight: 700;

        &:after {
          position: absolute;
          right: 20%;
          bottom: -1px;
          left: 20%;
          height: 2px;
          background: #18aee0;
          content: '';
        }
      }

      &:focus-visible {
        outline: 2px solid #38bdf8;
        outline-offset: -2px;
      }
    }
  }

  &__store-panel {
    width: 100%;
  }

  &__store-list {
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__store-item {
    display: grid;
    grid-template-columns: 7.6rem minmax(0, 1fr);
    gap: 0.9rem;
    padding: 0.8rem;
    border: 1px solid #edf1f4;
    border-radius: 1.1rem;
    background: #fbfcfd;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.035);
    cursor: pointer;

    > img {
      display: block;
      width: 7.6rem;
      height: 7.6rem;
      border-radius: 1rem;
      object-fit: cover;
      background: #f3f6f8;
    }
  }

  &__store-copy {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;

    strong {
      width: 100%;
      overflow: hidden;
      color: #1e293b;
      font-size: 1.4rem;
      font-weight: 700;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    p {
      display: -webkit-box;
      margin: 0.5rem 0 0;
      overflow: hidden;
      color: #64748b;
      font-size: 1.2rem;
      line-height: 1.45;
      word-break: break-word;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
  }

  &__store-status {
    margin-top: 0.4rem;
    font-size: 1.1rem;
    font-weight: 600;

    &--open {
      color: #047857;
    }

    &--holiday {
      color: #a16207;
    }

    &--close {
      color: #64748b;
    }
  }

  &__more {
    align-self: center;
    width: auto;
    min-height: 3.4rem;
    margin: 0.6rem auto 0;
    padding: 0.4rem 0.8rem;
    border: 0;
    background: transparent;
    color: #0b91bf;
    font-size: 1.3rem;
    font-weight: 700;

    &:focus-visible {
      outline: 2px solid #38bdf8;
      outline-offset: 1px;
    }
  }

  &__service-area {
    width: 100%;
    padding: 0.8rem 1rem;
    border: 1px solid #edf1f4;
    border-radius: 1rem;
    background: #f8fafb;

    h2 {
      margin: 0 0 0.7rem;
      color: #1e293b;
      font-size: 1.4rem;
      font-weight: 700;
      line-height: 1.35;
    }

    ul {
      display: flex;
      flex-wrap: wrap;
      gap: 0.6rem;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    button {
      width: auto;
      min-height: 2.8rem;
      padding: 0.4rem 0.8rem;
      border: 1px solid #d9e5eb;
      border-radius: 999px;
      background: #ffffff;
      color: #475569;
      font-size: 1.2rem;
    }
  }

  &__board,
  &__best-order {
    width: 100%;
    padding: 0;
    background: #ffffff;
  }

  &__board {
    padding-top: 1rem;
    border-top: 0.3rem solid #dceff6;
  }

  &__section-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.3rem;

    h2 {
      flex: 1 1 auto;
      width: auto;
      min-width: 0;
      margin: 0;
      overflow: hidden;
      color: #1e293b;
      font-size: 1.6rem;
      font-weight: 700;
      line-height: 1.35;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    button {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      gap: 0.3rem;
      width: auto !important;
      max-width: fit-content;
      padding: 0.4rem;
      border: 0;
      background: transparent;
      color: #64748b;
      font-size: 1.2rem;
      white-space: nowrap;

      &:focus-visible {
        outline: 2px solid #38bdf8;
        outline-offset: 1px;
      }
    }
  }

  &__board-list {
    margin: 0;
    padding: 0;
    list-style: none;

    > li {
      display: grid;
      grid-template-columns: minmax(0, 1fr) max-content;
      align-items: center;
      gap: 1rem;
      padding: 0.9rem 0;
      border-bottom: 1px solid #eef2f7;
      cursor: pointer;

      &:last-child {
        border-bottom: 0;
      }

      p {
        display: block;
        min-width: 0;
        width: 100%;
        margin: 0;
        overflow: hidden;
        color: #334155;
        font-size: 1.2rem;
        line-height: 1.35;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      span {
        display: block;
        margin: 0;
        color: #94a3b8;
        font-size: 1.2rem;
        line-height: 1.35;
        white-space: nowrap;
      }
    }
  }

  &__product-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.8rem;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      min-width: 0;
      cursor: pointer;
    }

    img {
      display: block;
      width: 100%;
      aspect-ratio: 1;
      border-radius: 0.8rem;
      object-fit: cover;
      background: #f3f6f8;
    }

    strong,
    span {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    strong {
      margin-top: 0.5rem;
      color: #334155;
      font-size: 1.2rem;
    }

    span {
      margin-top: 0.2rem;
      color: #0b91bf;
      font-size: 1.3rem;
      font-weight: 700;
    }
  }

  &__certification {
    display: flex;
    justify-content: center;
    padding: 1rem 0 0.4rem;
    border-top: 0.3rem solid #dceff6;
    text-align: center;

    a {
      display: inline-block;
    }

    img {
      display: block;
      width: auto;
      max-width: 13rem;
      max-height: 4.8rem;
      object-fit: contain;
    }
  }

  :deep(.empty-wrap) {
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 2.8rem;
  }

  :deep(.empty) {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 2.8rem;
    margin: 0.3rem 0 !important;
    padding: 0;
    color: #94a3b8;
    font-size: 1.2rem;
    line-height: 1.35;
  }

  :deep(.empty .empty-img) {
    min-height: 0;
    font-size: 1.2rem;
  }

  :deep(.empty img) {
    width: 1.8rem;
    height: 1.8rem;
  }
}
</style>

/* *Created on 2023-08-28 *ProjectName user */

<script>
export default {
  name: 'MAIN'
};
</script>
